import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ActionMenuList, IconButton, ListItem } from '@ds';
import {
  DSCloseIcon,
  DSEditIcon,
  DSFullScreenIcon,
  DSRemoveIcon
} from '@ds-icons';
import { DragIcon } from '@Icons-outdated';
import { Panel as PanelType } from '@mpm-druid/types';
import { useModelViewStore } from '../modelViewStore';
import { Panel } from '../Panel';
import { PanelModal } from '../PanelModal';

import styles from './PanelGrid.module.scss';

type PanelGridItemProps = {
  panel: PanelType;
  sectionId: string;
};

function PanelGridItemComponent({ panel, sectionId }: PanelGridItemProps) {
  const removeSectionPanel = useModelViewStore(
    state => state.removeSectionPanel
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const handleRemovePanel = useCallback(() => {
    removeSectionPanel(sectionId, panel.id);
  }, [removeSectionPanel, sectionId, panel.id]);

  const handleEditPanel = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleFullScreenToggle = useCallback(() => {
    if (!panelRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      panelRef.current?.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    window.addEventListener('fullscreenchange', onFullScreenChange);

    return () => {
      window.removeEventListener('fullscreenchange', onFullScreenChange);
    };
  }, []);

  return (
    <>
      <div
        className={styles.panelGridItem}
        data-test="panel-grid-item"
        ref={panelRef}
      >
        {!isFullScreen ? (
          <div className={styles.panelDragHandle}>
            <DragIcon />
          </div>
        ) : null}
        {!isFullScreen ? (
          <div
            className={styles.panelHeader}
            data-test="panel-grid-item-header"
          >
            <div className={styles.panelName} data-test="panel-grid-item-name">
              {panel.name}
            </div>

            <div className={styles.panelActions}>
              <IconButton
                onClick={handleFullScreenToggle}
                type="secondary"
                Icon={<DSFullScreenIcon />}
              />

              <ActionMenuList>
                <ActionMenuList.Trigger data-test="panel-grid-item-actions" />
                <ActionMenuList.Body>
                  <ListItem
                    onClick={handleEditPanel}
                    dataTest="panel-grid-item-actions-edit"
                  >
                    <ListItem.PrefixContainer>
                      <ListItem.Icon Icon={<DSEditIcon />} />
                    </ListItem.PrefixContainer>
                    <ListItem.Text primary="Edit" />
                  </ListItem>
                  <ListItem
                    onClick={handleRemovePanel}
                    dataTest="panel-grid-item-actions-delete"
                  >
                    <ListItem.PrefixContainer>
                      <ListItem.Icon Icon={<DSRemoveIcon />} />
                    </ListItem.PrefixContainer>
                    <ListItem.Text primary="Delete" />
                  </ListItem>
                </ActionMenuList.Body>
              </ActionMenuList>
            </div>
          </div>
        ) : (
          <div className={styles.panelFullScreenHeader}>
            <div className={styles.panelName}>{panel.name}</div>

            <IconButton
              onClick={handleFullScreenToggle}
              size="XL"
              type="secondary"
              Icon={<DSCloseIcon />}
            />
          </div>
        )}
        <div className={styles.panel} data-test="panel-grid-item-content">
          <Panel panel={panel} />
        </div>
      </div>
      {isEditing ? (
        <PanelModal
          onClose={() => setIsEditing(false)}
          panel={panel}
          sectionId={sectionId}
        />
      ) : null}
    </>
  );
}

export const PanelGridItem = React.memo(PanelGridItemComponent);
